import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import React, { useMemo } from 'react';
import Layout from '../layouts';
import { AppSettings, PageType } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { fetchLoadingDataWith, getArticleRecommendListInner } from '@/lib/service';
import { AllSupportLanguages } from '@/shared/language';
import { canonicalUrl, isBlogWeb } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { homeJsonLd } from '@/lib/jsonld';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';

const HomeContent = dynamic(() => import('@/components/Home'), {
  ssr: true,
});

export const getStaticProps: GetStaticProps = async (context) => {
  // 如果当前是博客子域名，则重定向到博客页面
  if (isBlogWeb()) {
    return {
      redirect: {
        destination: '/instagram-tracking-blog',
        permanent: false,
      },
    };
  }

  const { locale } = context;
  const purePathUsingUrl = '/';

  // 1. 并行请求数据，添加 .catch() 防止构建失败
  const [localeRes, tdkRes, dataResRaw, articleListRaw] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(purePathUsingUrl, locale),
    fetchLoadingDataWith('/', locale).catch((): null => null),
    getArticleRecommendListInner(null, locale).catch((): null => null),
  ]);

  // 2. 语言 Fallback：当前语言无数据时请求英文
  let dataRes = dataResRaw;
  if (!dataRes?.data && locale !== 'en') {
    dataRes = await fetchLoadingDataWith('/', 'en').catch((): null => null);
  }

  let articleList = articleListRaw;
  if (!articleList?.data && locale !== 'en') {
    articleList = await getArticleRecommendListInner(null, 'en').catch((): null => null);
  }

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const jsonLd = homeJsonLd({
    title: tdkRes?.data?.title ?? tempData?.head_Meta_Title,
    desc: tdkRes?.data?.description ?? tempData?.head_Meta_Desc,
    url: canonicalUrl(SITE_HOST, '/', locale),
  });

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, '/', locale),
      locale,
      articleList: articleList?.data ?? [],
      title: tdkRes?.data?.title ?? tempData?.head_Meta_Title,
      description: tdkRes?.data?.description ?? tempData?.head_Meta_Desc,
      pageType: PageType.Normal,
      contentData: dataRes?.data ?? null,
      jsonLd,
      // ISR 调试信息
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('home'),
      __isr_page_name: 'home',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('home'),
  };
};

const Home: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <HomeContent data={props} />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

Home.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;
  const jsonLd = page?.props?.jsonLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={'title'} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}

          {AllSupportLanguages.map((lang) => (
            <link
              key={lang.code}
              rel="alternate"
              hrefLang={lang.code}
              href={`https://www.dolphinradar.com/${lang.code}`}
            />
          ))}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Home;
